import { useAuiState } from '@assistant-ui/react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { useSession } from '@/lib/auth/useSession';
import { DOWN_REASONS, type Rating } from './api';
import { useMessageFeedback, useSubmitFeedback } from './queries';

/**
 * Thumbs under a finished answer (P1-D10). Up saves at once; down opens
 * "What was wrong?" with reason chips and a note. Shown on finished answers
 * only, and only once the conversation is registered in sreg-api.
 */
export function FeedbackBar() {
  const vertexSessionId = useAuiState((s) => s.threadListItem.remoteId);
  const messageRef = useAuiState((s) => s.message.id);
  const done = useAuiState((s) => s.message.status?.type !== 'running');
  const { can } = useSession();
  const existing = useMessageFeedback(vertexSessionId, messageRef);
  const submit = useSubmitFeedback();
  const [open, setOpen] = useState(false);

  if (!done || !vertexSessionId || !can('feedback.write')) return null;

  const rate = (rating: Rating, extra: { reasons?: string[]; note?: string } = {}) =>
    submit.mutate({ vertexSessionId, messageRef, rating, ...extra });

  const btn = (active: boolean) =>
    `rounded-md p-1.5 transition-colors ${active ? 'bg-sage/15 text-sage-dark' : 'text-ink-faint hover:bg-surface-sunken hover:text-ink'}`;

  return (
    <div className="mt-2 border-t border-line-soft pt-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Good answer"
          aria-pressed={existing?.rating === 'up'}
          className={btn(existing?.rating === 'up')}
          onClick={() => {
            setOpen(false);
            rate('up');
          }}
        >
          <ThumbsUp size={14} />
        </button>
        <button
          type="button"
          aria-label="Something was wrong"
          aria-pressed={existing?.rating === 'down'}
          className={btn(existing?.rating === 'down')}
          onClick={() => setOpen((o) => !o)}
        >
          <ThumbsDown size={14} />
        </button>
        {existing && !open && <span className="ml-1 text-xs text-ink-faint">Thanks — noted.</span>}
      </div>
      {open && (
        <WhatWasWrong
          initialReasons={existing?.rating === 'down' ? (existing.reasons ?? []) : []}
          initialNote={existing?.rating === 'down' ? (existing.note ?? '') : ''}
          busy={submit.isPending}
          onCancel={() => setOpen(false)}
          onSend={(reasons, note) => {
            rate('down', { reasons, note: note || undefined });
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function WhatWasWrong({
  initialReasons,
  initialNote,
  busy,
  onCancel,
  onSend,
}: {
  initialReasons: string[];
  initialNote: string;
  busy: boolean;
  onCancel: () => void;
  onSend: (reasons: string[], note: string) => void;
}) {
  const [reasons, setReasons] = useState<string[]>(initialReasons);
  const [note, setNote] = useState(initialNote);
  const toggle = (r: string) => setReasons((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));

  return (
    <div className="mt-2 rounded-xl border border-line bg-surface-alt p-3">
      <p className="text-sm font-semibold text-ink">What was wrong?</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {DOWN_REASONS.map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={reasons.includes(r)}
            onClick={() => toggle(r)}
            className={`rounded-full border px-2.5 py-1 text-xs ${reasons.includes(r) ? 'border-ink bg-ink text-white' : 'border-line bg-surface text-ink-soft hover:border-ink-faint'}`}
          >
            {r}
          </button>
        ))}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="What should Simon have said? (optional)"
        aria-label="Feedback note"
        className="mt-2 w-full resize-none rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-ink outline-none focus:border-sage"
      />
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3 py-1.5 text-xs font-semibold text-ink-soft hover:bg-surface-sunken"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy || (reasons.length === 0 && !note.trim())}
          onClick={() => onSend(reasons, note.trim())}
          className="rounded-lg bg-ink px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
        >
          Send
        </button>
      </div>
    </div>
  );
}
